import { useState } from "react";
import { Tag, Trash2, GripVertical, Upload, PlayCircle, VideoOff, Plus, Crop } from "lucide-react";
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors,
} from "@dnd-kit/core";
import {
  SortableContext, useSortable, arrayMove, rectSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { ImageCropModal } from "@/admin/ImageCropModal";
import { useImageCrop } from "@/admin/useImageCrop";
import { AltTextDialog } from "@/admin/AltTextDialog";
import { extractYoutubeId } from "@/lib/youtube";

const SortableThumb = ({ img, index, onEditAlt, onReCrop, onRemove }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: `${img.src}-${index}` });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  const isVideo = img.type === "video";

  return (
    <div ref={setNodeRef} style={style} data-testid={`gallery-manage-thumb-${index}`} className="relative group border border-[var(--border-default)]">
      <div className="aspect-square overflow-hidden bg-[var(--bg-tertiary)]">
        <img src={isVideo ? img.poster : img.src} alt={img.alt || `Gallery ${index + 1}`} className="w-full h-full object-cover" style={{ objectPosition: img.pos }} />
      </div>
      {isVideo && (
        <div data-testid={`gallery-manage-video-badge-${index}`} className="absolute top-1 right-1 bg-black/70 text-[var(--brand-primary)] text-[10px] font-mono px-1.5 py-0.5 flex items-center gap-1">
          <PlayCircle size={10} /> Video
        </div>
      )}
      {!isVideo && !img.alt && (
        <div data-testid={`gallery-manage-no-alt-${index}`} className="absolute top-1 left-1 bg-black/70 text-amber-400 text-[10px] font-mono px-1.5 py-0.5 flex items-center gap-1" title="No alt text set — bad for SEO">
          <Tag size={10} /> No alt text
        </div>
      )}
      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
        <button type="button" {...attributes} {...listeners} data-testid={`gallery-manage-drag-${index}`} className="p-2 text-white hover:text-[var(--brand-primary)] cursor-grab">
          <GripVertical size={16} />
        </button>
        {!isVideo && (
          <button type="button" onClick={() => onReCrop(index)} data-testid={`gallery-manage-recrop-${index}`} className="p-2 text-white hover:text-[var(--brand-primary)]" title="Re-crop this photo">
            <Crop size={16} />
          </button>
        )}
        <button type="button" onClick={() => onEditAlt(index)} data-testid={`gallery-manage-alt-${index}`} className="p-2 text-white hover:text-[var(--brand-primary)]" title="Edit alt text">
          <Tag size={16} />
        </button>
        <button type="button" onClick={() => onRemove(index)} data-testid={`gallery-manage-remove-${index}`} className="p-2 text-white hover:text-red-400">
          <Trash2 size={16} />
        </button>
      </div>
    </div>
  );
};

export const GalleryManager = ({ gallery, onChange, onUpload, uploading, allowVideo = false }) => {
  const [altIndex, setAltIndex] = useState(null);
  const [reCropIndex, setReCropIndex] = useState(null);
  const [showAddVideo, setShowAddVideo] = useState(false);
  const [videoKind, setVideoKind] = useState("mp4"); // "mp4" | "youtube"
  const [videoSrc, setVideoSrc] = useState("");
  const [videoPoster, setVideoPoster] = useState("");
  const [batchFiles, setBatchFiles] = useState([]);
  const [batchAlt, setBatchAlt] = useState("");
  const [batchUploading, setBatchUploading] = useState(false);
  const [batchProgress, setBatchProgress] = useState({ current: 0, total: 0 });
  const [batchErrors, setBatchErrors] = useState([]);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const photoOptions = gallery.filter((g) => g.type !== "video");

  const handleCropConfirm = async (file) => {
    if (reCropIndex === null) {
      onUpload(file);
      return;
    }
    const index = reCropIndex;
    setReCropIndex(null);
    const original = gallery[index];
    const updated = await onUpload(file); // appends the re-cropped photo to the end and persists it
    if (!updated) return;
    const newSrc = updated[updated.length - 1].src;
    // Drop the temporarily-appended duplicate and put the new crop back in the original photo's spot.
    onChange(updated.slice(0, -1).map((g, i) => (i === index ? { ...original, src: newSrc } : g)));
  };

  const crop = useImageCrop(handleCropConfirm);

  const handleReCrop = (index) => {
    setReCropIndex(index);
    crop.selectUrl(gallery[index].src, `photo-${index + 1}.jpg`);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = gallery.findIndex((g, i) => `${g.src}-${i}` === active.id);
    const newIndex = gallery.findIndex((g, i) => `${g.src}-${i}` === over.id);
    onChange(arrayMove(gallery, oldIndex, newIndex));
  };

  const handleRemove = (index) => {
    if (gallery.length <= 1) return window.alert("A product needs at least one photo or video.");
    onChange(gallery.filter((_, i) => i !== index));
  };

  const handleSaveAlt = (alt) => {
    onChange(gallery.map((g, i) => (i === altIndex ? { ...g, alt } : g)));
  };

  const handleAddVideo = () => {
    if (!videoSrc.trim()) return;
    if (videoKind === "youtube") {
      const id = extractYoutubeId(videoSrc);
      if (!id) return window.alert("Couldn't read a YouTube video ID from that — paste the full URL or just the 11-character video ID.");
      // Stored as type "video" with a full youtube.com/embed URL as src — every
      // detail page's video-rendering logic recognizes this exact format
      // (either via a ytId() regex or a direct "youtube.com/embed" substring
      // check), so no separate "type" value or extra plumbing is needed.
      onChange([...gallery, { src: `https://www.youtube.com/embed/${id}`, poster: `https://img.youtube.com/vi/${id}/hqdefault.jpg`, type: "video", pos: "center", alt: null }]);
    } else {
      onChange([...gallery, { src: videoSrc.trim(), poster: videoPoster.trim() || null, type: "video", pos: "center", alt: null }]);
    }
    setVideoSrc("");
    setVideoPoster("");
    setShowAddVideo(false);
  };

  // Batch upload: files go straight to the server as-is (no crop step, per
  // admin's chosen UX — each photo can still be re-cropped individually
  // afterward via the existing per-thumb crop icon). Uploaded sequentially,
  // never in parallel, because each upload appends to a gallery snapshot
  // fetched server-side at request time — parallel calls would race and
  // silently drop photos. The optional shared alt text is applied to all of
  // them in a single final `onChange` once every upload has settled.
  const handleBatchUpload = async () => {
    const files = batchFiles;
    const alt = batchAlt.trim();
    setBatchFiles([]);
    setBatchUploading(true);
    setBatchErrors([]);
    const failedNames = [];
    let latestGallery = null;
    let successCount = 0;
    for (let i = 0; i < files.length; i++) {
      setBatchProgress({ current: i + 1, total: files.length });
      const updated = await onUpload(files[i]);
      if (updated) {
        latestGallery = updated;
        successCount++;
      } else {
        failedNames.push(files[i].name);
      }
    }
    if (alt && latestGallery && successCount > 0) {
      onChange(latestGallery.map((g, i) => (i >= latestGallery.length - successCount ? { ...g, alt } : g)));
    }
    setBatchErrors(failedNames);
    setBatchAlt("");
    setBatchUploading(false);
    setBatchProgress({ current: 0, total: 0 });
  };

  return (
    <div>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={gallery.map((g, i) => `${g.src}-${i}`)} strategy={rectSortingStrategy}>
          <div className="grid grid-cols-4 gap-3" data-testid="gallery-manager-grid">
            {gallery.map((img, i) => (
              <SortableThumb key={`${img.src}-${i}`} img={img} index={i} onEditAlt={setAltIndex} onReCrop={handleReCrop} onRemove={handleRemove} />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      <p className="text-white/40 text-xs mt-3">Drag the grip handle to reorder — videos can be placed anywhere among the photos. Crop icon re-crops an existing photo, tag icon edits alt text. New single photos are cropped to 4:3 on upload; "Upload Multiple" skips the crop step (re-crop each one afterward if needed).</p>

      <div className="flex flex-wrap items-center gap-3 mt-3">
        <label className="inline-flex items-center gap-2 btn-secondary cursor-pointer" data-testid="gallery-upload-label">
          <Upload size={14} /> {uploading ? "Uploading…" : "Upload Photo"}
          <input
            type="file"
            accept="image/*"
            data-testid="gallery-upload-input"
            className="hidden"
            disabled={uploading || batchUploading}
            onChange={(e) => {
              if (e.target.files?.[0]) crop.selectFile(e.target.files[0]);
              e.target.value = "";
            }}
          />
        </label>

        <label className="inline-flex items-center gap-2 btn-secondary cursor-pointer" data-testid="gallery-batch-upload-label">
          <Upload size={14} /> Upload Multiple
          <input
            type="file"
            accept="image/*"
            multiple
            data-testid="gallery-batch-upload-input"
            className="hidden"
            disabled={uploading || batchUploading}
            onChange={(e) => {
              const files = Array.from(e.target.files || []);
              if (files.length > 0) setBatchFiles(files);
              e.target.value = "";
            }}
          />
        </label>

        {allowVideo && !showAddVideo && (
          <button type="button" onClick={() => setShowAddVideo(true)} data-testid="gallery-add-video-btn" className="btn-secondary">
            <Plus size={14} /> Add Video
          </button>
        )}
      </div>

      {batchFiles.length > 0 && !batchUploading && (
        <div data-testid="gallery-batch-upload-form" className="flex flex-col gap-3 mt-3 border border-[var(--border-default)] p-3 max-w-xl">
          <p className="text-white text-sm">{batchFiles.length} photo{batchFiles.length > 1 ? "s" : ""} selected</p>
          <input
            value={batchAlt}
            onChange={(e) => setBatchAlt(e.target.value)}
            placeholder="Alt text applied to all selected photos (optional)"
            data-testid="gallery-batch-alt-input"
            className="bg-transparent border border-white/15 px-3 py-2 text-sm text-white placeholder:text-white/40 focus:outline-none focus:border-[var(--brand-primary)]/70"
          />
          <p className="text-white/40 text-xs">Uploaded as-is (no crop step) — re-crop each photo individually afterward using its crop icon.</p>
          <div className="flex gap-2">
            <button type="button" onClick={handleBatchUpload} data-testid="gallery-batch-upload-confirm" className="btn-primary px-4">
              Upload {batchFiles.length} Photo{batchFiles.length > 1 ? "s" : ""}
            </button>
            <button type="button" onClick={() => { setBatchFiles([]); setBatchAlt(""); }} data-testid="gallery-batch-upload-cancel" className="p-2 text-white/50 hover:text-white">
              Cancel
            </button>
          </div>
        </div>
      )}

      {batchUploading && (
        <p data-testid="gallery-batch-upload-progress" className="text-white/60 text-xs font-mono mt-3">
          Uploading photo {batchProgress.current} of {batchProgress.total}…
        </p>
      )}

      {batchErrors.length > 0 && (
        <p data-testid="gallery-batch-upload-errors" className="text-red-400 text-xs mt-3">
          {batchErrors.length} photo{batchErrors.length > 1 ? "s" : ""} failed to upload: {batchErrors.join(", ")}
        </p>
      )}

      {allowVideo && showAddVideo && (
        <div data-testid="gallery-add-video-form" className="flex flex-col gap-3 mt-3 border border-[var(--border-default)] p-3 max-w-xl">
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => { setVideoKind("mp4"); setVideoSrc(""); setVideoPoster(""); }}
              data-testid="gallery-add-video-kind-mp4"
              className={`font-mono text-xs uppercase tracking-widest px-3 py-1.5 border ${videoKind === "mp4" ? "border-[var(--brand-primary)] text-[var(--brand-primary)]" : "border-white/15 text-white/50"}`}
            >
              Self-Hosted MP4
            </button>
            <button
              type="button"
              onClick={() => { setVideoKind("youtube"); setVideoSrc(""); setVideoPoster(""); }}
              data-testid="gallery-add-video-kind-youtube"
              className={`font-mono text-xs uppercase tracking-widest px-3 py-1.5 border ${videoKind === "youtube" ? "border-[var(--brand-primary)] text-[var(--brand-primary)]" : "border-white/15 text-white/50"}`}
            >
              YouTube Link
            </button>
          </div>
          <input
            value={videoSrc}
            onChange={(e) => setVideoSrc(e.target.value)}
            placeholder={videoKind === "youtube" ? "YouTube URL or video ID (e.g. mvahkXUwpA4)" : "Video URL (e.g. /videos/awnings/name.mp4)"}
            data-testid="gallery-add-video-src"
            className="bg-transparent border border-white/15 px-3 py-2 text-sm text-white placeholder:text-white/40 focus:outline-none focus:border-[var(--brand-primary)]/70"
          />
          {videoKind === "mp4" && (
            <div>
              <p className="text-white/50 text-xs mb-2">Poster image — pick one of this product's existing photos:</p>
              {photoOptions.length === 0 ? (
                <p className="text-amber-400 text-xs">Upload at least one photo first to pick a poster.</p>
              ) : (
                <div className="grid grid-cols-6 gap-2" data-testid="gallery-add-video-poster-picker">
                  {photoOptions.map((img, i) => (
                    <button
                      type="button"
                      key={`${img.src}-${i}`}
                      onClick={() => setVideoPoster(img.src)}
                      data-testid={`gallery-add-video-poster-option-${i}`}
                      className={`aspect-square overflow-hidden border-2 transition-colors ${
                        videoPoster === img.src ? "border-[var(--brand-primary)]" : "border-transparent hover:border-white/30"
                      }`}
                    >
                      <img src={img.src} alt={img.alt || `Photo ${i + 1}`} className="w-full h-full object-cover" style={{ objectPosition: img.pos }} />
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          {videoKind === "youtube" && (
            <p className="text-white/40 text-xs">The thumbnail is pulled automatically from YouTube — no need to pick a poster.</p>
          )}
          <div className="flex gap-2">
            <button type="button" onClick={handleAddVideo} data-testid="gallery-add-video-confirm" className="btn-primary px-4">Add</button>
            <button type="button" onClick={() => { setShowAddVideo(false); setVideoSrc(""); setVideoPoster(""); }} data-testid="gallery-add-video-cancel" className="p-2 text-white/50 hover:text-white">
              <VideoOff size={16} />
            </button>
          </div>
        </div>
      )}

      <ImageCropModal
        open={crop.isOpen}
        imageSrc={crop.imageSrc}
        fileName={crop.fileName}
        onCancel={crop.cancel}
        onConfirm={crop.confirm}
      />

      <AltTextDialog
        open={altIndex !== null}
        onOpenChange={(open) => !open && setAltIndex(null)}
        image={altIndex !== null ? gallery[altIndex] : null}
        onSave={handleSaveAlt}
      />
    </div>
  );
};

